import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { EMPTY, from, Observable } from 'rxjs';
import { delay } from 'rxjs/operators';
import { AssetContextService } from '../services/asset-context.service';
import { AuthService } from '../services/auth.service';
import { PermissionService } from '../services/permission.service';
import { UserImpersonationService } from '../services/user-impersonation.service';
import { AssetTypeStore } from '../stores/asset-type.store';
import { AssetStore } from '../stores/asset.store';
import { UserStore } from '../stores/user.store';

@Injectable({
  providedIn: 'root',
})
export class AssetResolver implements Resolve<void> {
  private readonly assetStore = inject(AssetStore);
  private readonly assetTypeStore = inject(AssetTypeStore);
  private readonly assetContext = inject(AssetContextService);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslocoService);
  private readonly authService = inject(AuthService);
  private readonly impersonationService = inject(UserImpersonationService);
  private readonly userStore = inject(UserStore);
  private readonly permissionService = inject(PermissionService);

  resolve(route: ActivatedRouteSnapshot): Observable<void> {
    const id = Number(route.paramMap.get('id'));

    if (isNaN(id) || id <= 0) {
      void this.router.navigate(['/404']);
      return EMPTY;
    }

    const work: Promise<void> = this.assetStore
      .loadById(id)
      .then(async () => {
        const asset = this.assetStore.byId()(id);
        if (!asset) {
          throw new Error('Asset not found');
        }

        this.assetContext.setCurrentAsset(id);

        const tasks: Promise<unknown>[] = [];
        if (asset.assetTypeId) {
          tasks.push(this.assetTypeStore.loadById(asset.assetTypeId));
        }
        tasks.push(this.warmPermissionCache(id));
        await Promise.all(tasks);
      })
      .catch((error: unknown) => {
        console.error(this.translateService.translate('common.errors.assetLoadFailed', { id }), error);
        void this.router.navigate(['/404']);
        return undefined;
      });

    return from(work).pipe(delay(0));
  }

  private async warmPermissionCache(assetId: number): Promise<void> {
    await this.authService.waitForUsers();
    const effectiveUserId = this.impersonationService.getEffectiveUserId(this.authService.currentUserId());
    if (!effectiveUserId) return;

    const isImpersonating = this.impersonationService.isImpersonating();
    const user = this.userStore.byId()(effectiveUserId);
    if (user?.isAdmin && !isImpersonating) return;

    await this.permissionService.getUserPermissionsForAsset(effectiveUserId, assetId);
  }
}
